/**
 * The core language a sharing form desugars into (SICP §4.1.2): every slot
 * stands for a few statements, each a `define` or an `emit`, over a small set
 * of expressions. `SlotLogic.asStatements` does the desugaring per slot type;
 * `ComposeLogic.evaluateForm` runs the statements in form order. A topic
 * type's `definitions.yaml` is a list of defines too, run by the same
 * `StatementLogic.run` over the share environment.
 *
 *   define  binds a name to an expression's value for the statements after
 *           it, with an optional doc saying what a person calls it.
 *   emit    puts an expression's value into the message body.
 *
 * The expressions they define and emit are in `expression.ts`.
 */

import { EnvironmentLogic, type Environment, type Thunk } from './environment';
import { ExpressionLogic, type Expression } from './expression';

export type Statement =
  | {
      readonly kind: 'define';
      readonly name: string;
      readonly expr: Expression;
      /** What a person calls the value, recorded beside the binding. */
      readonly doc?: string;
    }
  | { readonly kind: 'emit'; readonly expr: Expression };

/** A define statement. */
export type Define = Extract<Statement, { kind: 'define' }>;

/** Where an emit's expression goes, with the environment it evaluates in. */
export type Emit = (expr: Expression, env: Environment) => void;

/**
 * What a define binds its name to: a thunk of its expression in `env`,
 * evaluated the first time it is looked up (see `Thunk`).
 */
function delayed(expr: Expression, env: Environment): Thunk {
  return EnvironmentLogic.delay(at => ExpressionLogic.evaluate(expr, at), env);
}

/**
 * The environment after `statement` runs in `env`: a define binds its name
 * for what follows (`EnvironmentLogic.defineVariable`), and an emit hands its
 * expression and `env` to `emit`.
 */
function execute<F>(
  statement: Statement,
  env: Environment<F>,
  emit?: Emit
): Environment<F> {
  if (statement.kind === 'emit') {
    emit?.(statement.expr, env);
    return env;
  }
  return EnvironmentLogic.defineVariable(
    env,
    statement.name,
    delayed(statement.expr, env),
    statement.doc
  );
}

/** SICP's eval-sequence: `statements` executed in order from `env`. */
function run<F>(
  statements: readonly Statement[],
  env: Environment<F>,
  emit?: Emit
): Environment<F> {
  return statements.reduce(
    (current, statement) => execute(statement, current, emit),
    env
  );
}

export const StatementLogic = { delayed, execute, run };
